<script lang="ts">
    import Menu from './components/Menu.svelte';
    import InteractiveTitle from './components/InteractiveTitle.svelte';
    import InteractiveSubtitle from './components/InteractiveSubtitle.svelte';
    import { createEventDispatcher } from 'svelte';

    import type { InteractiveTitleSettingDefinition } from './types';
    import type { TitleAlign } from 'portlet/models/settings/settingDefinitions/titleAlignSetting';

    interface Props {
        title?: string;
        subtitle?: string;
        titleAlign: TitleAlign;
        replacementTags?: Record<string, string>;
        interactiveReplacementTags?: Record<string, InteractiveTitleSettingDefinition>;
        nomenu?: boolean;
        maximized?: boolean;
        jumpToAOEnabled?: boolean;
        showSupportForm?: boolean;
        externalInfoUrlEnabled?: boolean;
        isPerformanceLow?: boolean;
        isDataToDownload?: boolean;
    }

    let {
        title = 'No title',
        subtitle = '',
        titleAlign,
        replacementTags = {},
        interactiveReplacementTags = {},
        nomenu = false,
        maximized = false,
        jumpToAOEnabled = false,
        showSupportForm = true,
        externalInfoUrlEnabled = false,
        isPerformanceLow = false,
        isDataToDownload = false,
    }: Props = $props();

    let left = $derived(titleAlign === 'left');
    let right = $derived(titleAlign === 'right');
    let center = $derived(titleAlign === 'center');

    const dispatch = createEventDispatcher();

    const handleTitleDblClick = () => {
        dispatch('portlet-maximize');
        // this allows user to select title and not get annoyed by the selection when doubleclicking
        window.getSelection()?.removeAllRanges();
    };

    function handleDragEnter(e: DragEvent) {
        (e.target as HTMLElement)?.classList.add('on-drag');
    }
</script>

<header
    class="header"
    class:on-drag={false}
    class:center
    class:small-header={!title.trim() && !subtitle.trim()}
    data-testid="header"
    ondblclick={handleTitleDblClick}
    ondragenter={handleDragEnter}
>
    <span class="header__section header__section--left" class:center>
        <slot name="header-prefix" />
    </span>
    <div class="title" class:center class:left class:right>
        <InteractiveTitle
            {title}
            {replacementTags}
            {interactiveReplacementTags}
            {titleAlign}
            on:interactive-title-change
        />
        {#if subtitle}
            <InteractiveSubtitle
                {subtitle}
                {replacementTags}
                {interactiveReplacementTags}
                {titleAlign}
                on:interactive-title-change
            />
        {/if}
    </div>
    <span class="header__section header__section--right" class:center>
        <slot name="header-postfix" />
        {#if !nomenu}
            <Menu
                {externalInfoUrlEnabled}
                {maximized}
                {jumpToAOEnabled}
                {showSupportForm}
                {isPerformanceLow}
                {isDataToDownload}
                on:portlet-maximize
                on:jump-to-ao
                on:portlet-menu-item-click
                on:open-info-url
                on:download-data
            />
        {/if}
    </span>
</header>

<style lang="scss">
    .title {
        flex-grow: 1;
        flex-shrink: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: inline-block;
        flex-direction: column;
        align-items: center;
        align-content: space-around;
        justify-content: center;
        &.center {
            flex-grow: 0;
            text-align: center;
        }
        &.left {
            flex-grow: 1;
            text-align: left;
        }
        &.right {
            flex-grow: 1;
            text-align: right;
        }
    }

    .header {
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 0;
        justify-content: space-between;
        margin: 0 1rem;
        margin-bottom: 0.3rem;
        min-height: var(--header-min-height);
        z-index: 1;
        &.on-drag {
            z-index: 0;
        }

        &.small-header {
            min-height: auto;
            margin-bottom: 0;
        }

        &__section {
            display: flex;
            align-items: center;
            justify-content: flex-end;

            &.center {
                text-align: center;
                flex-grow: 1;
                flex-shrink: 1;
                flex-basis: 0;
            }

            &--left {
                left: 0;
                text-align: left;
                justify-content: flex-start;
                flex-grow: 0;
            }

            &--right {
                right: 0;
                flex-grow: 0;
                justify-content: flex-end;
                text-align: right;
            }
        }
    }
</style>
